import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import {
  rentalPaymentErrorToastKey,
  rentalTransferGateDenial,
  type TransferGateDenyCode,
} from '../../../api/rentals';
import { useRentalPaymentWrite } from '../../../hooks/use-rental-payment';
import { PaymentMethodSelect } from './PaymentMethodSelect';
import { TransferEligibilityNotice } from './TransferEligibilityNotice';

type PaymentMethod = 'CASH' | 'CARD' | 'TRANSFER';

interface RentalPaymentFormProps {
  rentalId: string;
  outstanding: number;
  onSuccess: (newOutstanding: number) => void;
  onCancel?: (() => void) | undefined;
}

export function RentalPaymentForm({
  rentalId,
  outstanding,
  onSuccess,
  onCancel,
}: RentalPaymentFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { recordPayment, loading } = useRentalPaymentWrite();

  const [amount, setAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH');
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  // L1(b): a TRANSFER-gate reaktív tükre — a 400 { code, reason } inline a selector alatt.
  const [transferGate, setTransferGate] = useState<{
    code: TransferGateDenyCode;
    reason: string;
  } | null>(null);

  // Fizetési-mód váltás → a korábbi gate-figyelmeztetés eltűnik (a state elavul).
  const handlePaymentMethodChange = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setTransferGate(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTransferGate(null);
    try {
      const response = await recordPayment(rentalId, {
        amount,
        paymentMethod,
        paymentReference: paymentReference || undefined,
        notes: notes || undefined,
      });
      toast({
        title: t('rental.payment.success'),
        variant: 'default',
      });
      onSuccess(response.data.outstanding);
    } catch (err) {
      // L1(b): TRANSFER-gate elutasítás → inline tükör a selectornél (nem toast); a többi hiba toast.
      const denial = rentalTransferGateDenial(err);
      if (denial) {
        setTransferGate(denial);
        return;
      }
      const toastKey = rentalPaymentErrorToastKey(err);
      toast({
        title: t(toastKey),
        variant: 'destructive',
      });
    }
  };

  const isDisabled = amount <= 0 || loading;

  return (
    <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
      <div>
        <p className="text-sm text-muted-foreground">
          {t('rental.payment.outstandingBalance')}: {formatCurrency(outstanding)}
        </p>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('rental.payment.amount')}</label>
        <input
          type="number"
          data-testid="payment-amount-input"
          value={amount || ''}
          onChange={e => setAmount(Number(e.target.value) || 0)}
          max={outstanding}
          min={0}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div className="space-y-2">
        <PaymentMethodSelect
          value={paymentMethod}
          onChange={handlePaymentMethodChange}
          disabled={loading}
        />
        <TransferEligibilityNotice code={transferGate?.code} reason={transferGate?.reason} />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('rental.payment.reference')}</label>
        <input
          type="text"
          data-testid="payment-reference-input"
          value={paymentReference}
          onChange={e => setPaymentReference(e.target.value)}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('rental.payment.notes')}</label>
        <textarea
          data-testid="payment-notes-input"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
          rows={3}
        />
      </div>

      <div className="flex gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 rounded-md border border-input px-4 py-2 text-sm"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          data-testid="payment-submit"
          disabled={isDisabled}
          className="flex-1 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {t('rental.payment.submit')}
        </button>
      </div>
    </form>
  );
}
